import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeftRight, Store, ReceiptText, TrendingUp, Wallet, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

function fmtMoney(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString()}`;
}

function metricCard({ label, value, icon: Icon, note }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{label}</p>
            <p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{value}</p>
            {note ? <p className="text-xs text-slate-400 mt-1">{note}</p> : null}
          </div>
          <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <Icon size={20} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState({});
  const [currency, setCurrency] = useState('UGX');

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get('/dashboard/packages'),
      api.get('/dashboard/overview'),
    ])
      .then(([pkgRes, overviewRes]) => {
        if (!mounted) return;
        setAccess(pkgRes.data?.access || {});
        setSummary(overviewRes.data || {});
        setCurrency(overviewRes.data?.wallets?.[0]?.currency || overviewRes.data?.analytics?.currency || 'UGX');
        setAnalytics(overviewRes.data?.analytics || null);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load analytics');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!access?.can_use_analytics) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Analytics</h1>
          <p className="text-slate-500 text-sm mt-0.5">Upgrade your package to unlock Budget and Financial Intelligence.</p>
        </div>
        <Card className="bg-white border border-amber-200 shadow-sm rounded-2xl">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                Your package does not currently include analytics access. Upgrade to Premium to unlock Budget Intelligence.
              </p>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
                View Packages
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const bestSelling = analytics?.best_selling_products || [];
  const methods = analytics?.payment_methods_used || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Analytics</h1>
          <p className="text-slate-500 text-sm mt-0.5">View payment performance, store sales and link insights at a glance.</p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-800 self-start">Enabled</Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {metricCard({ label: 'Total Payments', value: summary?.total_transactions ?? 0, icon: Wallet, note: `${fmtMoney(analytics?.month_sales?.total || 0, currency)} this month` })}
        {metricCard({ label: 'Successful Transactions', value: analytics?.month_sales?.count ?? 0, icon: ArrowLeftRight, note: 'Confirmed this month' })}
        {metricCard({ label: 'Pending Transactions', value: analytics?.pending_payments ?? 0, icon: ReceiptText, note: 'Awaiting completion' })}
        {metricCard({ label: 'Failed Transactions', value: analytics?.failed_transactions ?? 0, icon: AlertCircle, note: 'Review payment logs' })}
        {metricCard({ label: 'Store Sales', value: analytics?.store_orders ?? 0, icon: Store, note: `${fmtMoney(analytics?.month_sales?.total || 0, currency)} this month` })}
        {metricCard({ label: 'Top Products', value: analytics?.best_selling_products?.length ?? 0, icon: TrendingUp, note: 'Performance highlights' })}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Link Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {methods.length ? methods.map((item) => (
              <div key={item.method} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <span className="text-sm text-slate-700 capitalize">{item.method?.replace(/_/g, ' ')}</span>
                <span className="text-sm font-semibold text-slate-900 mono-nums">{item.count}</span>
              </div>
            )) : (
              <p className="text-sm text-slate-500">No payment method data yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Best Selling Products</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {bestSelling.length ? bestSelling.map((item, index) => (
              <div key={`${item.product_name}-${index}`} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">{item.product_name || 'Product'}</span>
                  <span className="text-sm font-semibold text-slate-900 mono-nums">{item.quantity}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{fmtMoney(item.revenue || 0, currency)} revenue</p>
              </div>
            )) : (
              <p className="text-sm text-slate-500">No product performance data yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
